import React, {
	useCallback, useRef,
} from 'react';

import {
	DndProvider,
	useDrag,
	useDrop,
} from 'react-dnd';

import {
	HTML5Backend,
} from 'react-dnd-html5-backend';

import CustomIcon from '../Common/CustomIcon/CustomIcon';
import style from './VisualContentLoop.module.css';
import {
	formatDate,
	getCurrentDateString,
} from './utils/dateUtils';
import {
	createBackfillIndicator,
} from './utils/vclUtils';
import {
	useVisualContentLoopContext,
	SaveButton,
} from './VisualContentLoopContext';
import SlotsEditor from './components/visualContentLoop/SlotsEditor/SlotsEditor';
import AddContentModal from './components/visualContentLoop/AddContentModal/AddContentModal';
import ResolutionToggleIcon from './components/visualContentLoop/ResolutionToggleIcon/ResolutionToggleIcon';

const FolderChildRow = ({
	child, loopDate, style: s, formatDate: fd,
}) => {
	const [expanded, setExpanded] = React.useState(false);
	const folderFiles    = child.folderFiles || [];
	const folderLabel    = child.name.startsWith('Folder:')
		? child.name.substring('Folder:'.length).trim()
		: child.name;

	const _now = new Date();
	const _defaultDateStr = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;
	const compareDateStr = loopDate || _defaultDateStr;

	const isFileDelivering = (file) => {
		if (file.folder_file_active === false) return false;
		const ranges = file.folder_file_date_ranges;
		if (!Array.isArray(ranges) || ranges.length === 0) return true;
		return ranges.some(({ start_date, end_date }) => {
			if (start_date && compareDateStr < start_date) return false;
			if (end_date   && compareDateStr > end_date)   return false;
			return true;
		});
	};

	const deliveringFiles = folderFiles.filter(isFileDelivering);
	const deliveringCount = deliveringFiles.length;

	return (
		<React.Fragment>
			<div className={`${s.tableRow} ${s.childRow}`}>
				<div className={s.nameCell}>
					<span className={s.childName} data-test-id="loopDetailChildName" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
						<button
							type="button"
							aria-expanded={expanded}
							aria-label={expanded ? 'Collapse folder files' : 'Expand folder files'}
							onClick={() => setExpanded((v) => !v)}
							style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px 0 0', color: '#555', flexShrink: 0 }}
							title={expanded ? 'Collapse' : 'Expand files'}
						>
							<CustomIcon icon={expanded ? 'chevron-down' : 'chevron-right'} />
						</button>
						<CustomIcon icon={['far', 'folder']} style={{ flexShrink: 0 }} />
						<span>{folderLabel}</span>
						<span style={{
							fontSize:     '10px',
							fontWeight:   '700',
							background:   '#007bff',
							color:        '#fff',
							borderRadius: '10px',
							padding:      '1px 6px',
							whiteSpace:   'nowrap',
						}}>
							{deliveringCount} {loopDate && loopDate !== _defaultDateStr ? `DELIVERING ON ${compareDateStr}` : 'DELIVERING TODAY'}
						</span>
					</span>
				</div>
				<div className={s.durationCell}>{child.duration}</div>
				<div className={s.slotsCell}>{child.slots}</div>
				<div className={s.orderCell}></div>
				<div className={s.startDateCell}>{fd(child.startDate) || ''}</div>
				<div className={s.endDateCell}>{fd(child.endDate) || ''}</div>
				<div className={s.actionsCell}></div>
			</div>
			{expanded && deliveringFiles.map((file) => (
				<div key={file.id} className={`${s.tableRow} ${s.childRow}`} >
					<div className={s.nameCell}>
						<span className={s.childName} data-test-id="loopDetailChildName" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
							<span style={{ width: 8, height: 8, borderRadius: '50%', background: '#28a745', display: 'inline-block', flexShrink: 0 }} />
							{file.file_name || (file.path || '').split('/').pop() || 'Untitled'}
						</span>
					</div>
					<div className={s.durationCell}>{Number.isFinite(file.duration) ? Math.round(file.duration) : '-'}</div>
					<div className={s.slotsCell}>1</div>
					<div className={s.orderCell}></div>
					<div className={s.startDateCell}></div>
					<div className={s.endDateCell}></div>
					<div className={s.actionsCell}></div>
				</div>
			))}
		</React.Fragment>
	);
};

const VisualContentLoop = (props = {}) => {

	// Use shared context instead of creating new hook instance
	const {
		// State
		loopDate,
		setLoopDate,
		campaigns,
		setCampaigns,
		backfillContent,
		setBackfillContent,
		isBackfillCollapsed,
		setIsBackfillCollapsed,
		resolution,

		// Modal state
		showAddContentModal,
		addContentType,
		isLoadingSlideDecks,
		availableSlideDecks,
		availablePublishedFeeds,

		// Loading state
		isSaving,
		isRefreshing,

		// Computed values
		totalSlots,
		totalDuration,

		// Functions
		getProviderDuration,
		getOriginalSlidesForProvider,
		transformSlide,
		openAddContentModal,
		closeAddContentModal,
		handleAddProvider,
		handleRefresh,
		handleToggleResolution,
		// Provider types
		getProviderType,
	} = useVisualContentLoopContext();

	const toggleCampaignExpanded = async (campaignId) => {
		setCampaigns((prevCampaigns) => {
			return prevCampaigns.map((campaign) => {
				if (campaign.id === campaignId && !campaign.isDeleted) {
					return {
						...campaign,
						expanded: !campaign.expanded,
					};
				}
				return campaign;
			});
		});
	};

	const toggleBackfillCollapsed = () => {
		setIsBackfillCollapsed(!isBackfillCollapsed);
	};

	const toggleBackfillExpanded = async (itemId) => {
		setBackfillContent((prevBackfill) => {
			return prevBackfill.map((item) => {
				if (item.id === itemId && !item.isDeleted) {
					return {
						...item,
						expanded: !item.expanded,
					};
				}
				return item;
			});
		});
	};

	// Delete campaign function
	const deleteCampaign = (campaignId) => {
		// Mark campaign as deleted but don't remove from array
		setCampaigns((prevCampaigns) => {
			return prevCampaigns.map((campaign) => {
				if (campaign.id === campaignId) {
					return {
						...campaign,
						isDeleted: true,
						expanded:  false, // Collapse when deleted
					};
				}
				return campaign;
			});
		});
	};

	// Add function to undo deletion
	const undoDeleteCampaign = (campaignId) => {
		// Mark campaign as not deleted
		setCampaigns((prevCampaigns) => {
			return prevCampaigns.map((campaign) => {
				if (campaign.id === campaignId) {
					return {
						...campaign,
						isDeleted: false,
						expanded:  true, // Auto-expand when undoing deletion
					};
				}
				return campaign;
			});
		});
	};

	// Add function to undo deletion for backfill
	const undoDeleteBackfillContent = (backfillId) => {
		// Mark backfill content as not deleted
		setBackfillContent((prevBackfill) => {
			return prevBackfill.map((item) => {
				if (item.id === backfillId) {
					return {
						...item,
						isDeleted: false,
						expanded:  true, // Auto-expand when undoing deletion
					};
				}
				return item;
			});
		});
	};

	// Delete backfill content function
	const deleteBackfillContent = (backfillId) => {
		// Mark backfill content as deleted but don't remove from array
		setBackfillContent((prevBackfill) => {
			return prevBackfill.map((item) => {
				if (item.id === backfillId) {
					return {
						...item,
						isDeleted: true,
						expanded:  false, // Collapse when deleted
					};
				}
				return item;
			});
		});
	};

	// Update campaign slots function
	const updateCampaignSlots = async (campaignId, newSlots) => {
		setCampaigns((prevCampaigns) => {
			return prevCampaigns.map((campaign) => {
				if (campaign.id === campaignId) {
					const updatedCampaign = {
						...campaign,
						slots: Math.max(0, newSlots),
					};

					// If this is a slide deck (or published feed), we need to re-slice the children and recalculate backfill
					if (campaign.type === 'slide_deck' || campaign.type === 'published_feed') {
						// Re-fetch and slice the slides based on new slot count
						getOriginalSlidesForProvider(campaignId, campaign.type, props.visualContentLoop?.account?.uuid).then((originalSlides) => {
							// Slice by slot count, not item count (folders can take multiple slots)
							const maxSlots = Math.max(0, newSlots);
							let currentSlots = 0;
							const slicedSlides = [];
							for (const slide of originalSlides) {
								slide.folderSlots = slide.folderSlots ?? 1;
								const isFolder = slide.itemType === 'folder';
								const slideSlots = isFolder ? slide.folderSlots : 1;
								if (currentSlots + slideSlots <= maxSlots) {
									slicedSlides.push(slide);
									currentSlots += slideSlots;
								} else if (isFolder && slideSlots > 0 && maxSlots > currentSlots) {
									slide.folderSlots = maxSlots - currentSlots;
									slicedSlides.push(slide);
									currentSlots += slide.folderSlots;
									break;
								} else {
									break;
								}
							}
							const transformedSlides = slicedSlides.map(transformSlide).filter(Boolean);

							// Recalculate backfill indicators
							const availableSlots = Math.max(0, newSlots);
							const usedSlots = transformedSlides.reduce((total, slide) => {
								// Folders take up slide.slots slots (0 for empty folders), regular slides take 1 slot
								return total + (slide.itemType === 'folder' ? (slide.slots ?? 1) : 1);
							}, 0);
							const backfillSlots = Math.max(0, availableSlots - usedSlots);

							// Add backfill indicator if needed
							const childrenWithBackfill = [ ...transformedSlides, ];
							if (backfillSlots > 0 && !campaign.isBackfill) {
								childrenWithBackfill.push(createBackfillIndicator(campaignId, backfillSlots));
							}

							setCampaigns((prevCampaigns2) => {
								return prevCampaigns2.map((campaign2) => {
									if (campaign2.id === campaignId) {
										return {
											...campaign2,
											children: childrenWithBackfill,
										};
									}
									return campaign2;
								});
							});
						});
					} else {
						// For non-slide deck providers (dynamic providers), don't add backfill indicators
						// These providers manage their own content and slots dynamically
						const existingContent = (campaign.children || []).filter((child) => {
							return !child.isBackfillIndicator;
						});

						return {
							...updatedCampaign,
							children: existingContent,
						};
					}

					return updatedCampaign;
				}
				return campaign;
			});
		});
	};

	// Update backfill content slots function
	const updateBackfillSlots = async (backfillId, newSlots) => {
		setBackfillContent((prevBackfill) => {
			return prevBackfill.map((item) => {
				if (item.id === backfillId) {
					const updatedItem = {
						...item,
						slots: Math.max(0, newSlots),
					};

					// If this is a slide deck (or published feed), we need to re-slice the children and recalculate backfill
					if (item.type === 'slide_deck' || item.type === 'published_feed') {
						// Re-fetch and slice the slides based on new slot count
						getOriginalSlidesForProvider(backfillId, item.type, props.visualContentLoop?.account?.uuid).then((originalSlides) => {
							// Slice by slot count, not item count (folders can take multiple slots)
							const maxSlots = Math.max(0, newSlots);
							let currentSlots = 0;
							const slicedSlides = [];
							for (const slide of originalSlides) {
								slide.folderSlots = slide.folderSlots ?? 1;
								const isFolder = slide.itemType === 'folder';
								const slideSlots = isFolder ? slide.folderSlots : 1;
								if (currentSlots + slideSlots <= maxSlots) {
									slicedSlides.push(slide);
									currentSlots += slideSlots;
								} else if (isFolder && slideSlots > 0 && maxSlots > currentSlots) {
									slide.folderSlots = maxSlots - currentSlots;
									slicedSlides.push(slide);
									currentSlots += slide.folderSlots;
									break;
								} else {
									break;
								}
							}
							const transformedSlides = slicedSlides.map(transformSlide).filter(Boolean);

							// Backfill content never shows backfill indicators - they are the backfill
							setBackfillContent((prevBackfill2) => {
								return prevBackfill2.map((item2) => {
									if (item2.id === backfillId) {
										return {
											...item2,
											children: transformedSlides,
										};
									}
									return item2;
								});
							});
						});
					} else {
						// For non-slide deck providers (dynamic providers), don't add backfill indicators
						// These providers manage their own content and slots dynamically
						const existingContent = (item.children || []).filter((child) => {
							return !child.isBackfillIndicator;
						});

						return {
							...updatedItem,
							children: existingContent,
						};
					}

					return updatedItem;
				}
				return item;
			});
		});
	};

	// Drag and drop types
	const ItemTypes = {
		CAMPAIGN: 'campaign',
		BACKFILL: 'backfill',
	};

	// Move item function for drag and drop
	const moveItem = useCallback((dragIndex, hoverIndex, itemType) => {
		if (itemType === ItemTypes.CAMPAIGN) {
			setCampaigns((prevCampaigns) => {
				const draggedItem = prevCampaigns[dragIndex];
				const newItems = [
					...prevCampaigns,
				];
				newItems.splice(dragIndex, 1);
				newItems.splice(hoverIndex, 0, draggedItem);

				// Update order numbers
				return newItems.map((item, index) => {
					return {
						...item,
						order: index + 1,
					};
				});
			});
		} else if (itemType === ItemTypes.BACKFILL) {
			setBackfillContent((prevBackfill) => {
				const draggedItem = prevBackfill[dragIndex];
				const newItems = [
					...prevBackfill,
				];
				newItems.splice(dragIndex, 1);
				newItems.splice(hoverIndex, 0, draggedItem);

				// Update order numbers
				return newItems.map((item, index) => {
					return {
						...item,
						order: index + 1,
					};
				});
			});
		}
	}, [
		ItemTypes.CAMPAIGN,
		ItemTypes.BACKFILL,
		setCampaigns,
		setBackfillContent,
	]);

	// DraggableRow component for campaigns
	const DraggableCampaignRow = ({
		item,
		index,
		moveItemProp,
	}) => {
		const ref = useRef(null);

		const [
			{
				handlerId,
			},
			drop,
		] = useDrop({
			accept: ItemTypes.CAMPAIGN,
			collect(monitor) {
				return {
					handlerId: monitor.getHandlerId(),
				};
			},
			hover(dragItem, monitor) {
				if (!ref.current) {
					return;
				}
				const dragIndex = dragItem.index;
				const hoverIndex = index;

				// Don't replace items with themselves
				if (dragIndex === hoverIndex) {
					return;
				}

				// Determine rectangle on screen
				const hoverBoundingRect = ref.current?.getBoundingClientRect();

				// Get vertical middle
				const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

				// Determine mouse position
				const clientOffset = monitor.getClientOffset();

				// Get pixels to the top
				const hoverClientY = clientOffset.y - hoverBoundingRect.top;

				// Only perform the move when the mouse has crossed half of the items height
				// When dragging downwards, only move when the cursor is below 50%
				// When dragging upwards, only move when the cursor is above 50%

				// Dragging downwards
				if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
					return;
				}

				// Dragging upwards
				if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
					return;
				}

				// Time to actually perform the action
				moveItemProp(dragIndex, hoverIndex, ItemTypes.CAMPAIGN);

				// Note: we're mutating the monitor item here!
				// Generally it's better to avoid mutations,
				// but it's good here for the sake of performance
				// to avoid expensive index searches.
				dragItem.index = hoverIndex;
			},
		});

		const [
			{
				isDragging,
			},
			drag,
		] = useDrag({
			type: ItemTypes.CAMPAIGN,
			item: () => {
				return {
					id: item.id,
					index,
				};
			},
			collect: (monitor) => {
				return {
					isDragging: monitor.isDragging(),
				};
			},
		});

		const opacity = isDragging ? 0.5 : 1;
		drag(drop(ref));

		const isExpanded = item.expanded;
		const isDeleted = item.isDeleted;

		return (
			<div
				ref={ref}
				style={{
					opacity,
				}}
				data-handler-id={handlerId}
				className={`${style.draggableContainer} ${isDragging ? style.dragging : ''} ${isDeleted ? style.deletedItem : ''} ${(item.unavailable || item.suppressed) ? style.unavailableItem : ''}`}
			>
				<div className={style.tableRow}>
					<div className={style.nameCell}>
						{item.children && item.children.length > 0 ? (
							<button
								className={style.expandButton}
								onClick={() => {
									return toggleCampaignExpanded(item.id);
								}}
								disabled={isDeleted}
							>
								<CustomIcon icon={isExpanded ? 'chevron-down' : 'chevron-right'} />
							</button>
						) : (
							<div className={style.expandButton} style={{
								visibility: 'hidden',
							}}>
								<CustomIcon icon="chevron-right" />
							</div>
						)}
						{!isDeleted && (
							<div className={style.dragHandle}>
								<CustomIcon icon="bars" />
							</div>
						)}
						<span
							className={`${style.parentName} ${isDeleted ? style.strikethrough : ''}`}
							title={item.unavailable ? 'This feed has been unpublished. It no longer plays and will be removed from this loop on the next save.' : (item.suppressed ? 'This feed is temporarily unavailable (publishing disabled or publisher access changed). It is skipped on players and will resume if the source is restored.' : undefined)}
							data-test-id={item.unavailable ? 'vclUnavailableProvider' : (item.suppressed ? 'vclSuppressedProvider' : 'loopDetailParentName')}
						>
							{item.name}
						</span>
					</div>
					<div className={style.durationCell}>
						{getProviderDuration(item)}
					</div>
					<div className={style.slotsCell}>
						<SlotsEditor
							value={item.slots}
							onUpdate={updateCampaignSlots}
							itemId={item.id}
							itemType="campaign"
						/>
					</div>
					<div className={style.orderCell}>
						{item.order || ''}
					</div>
					<div className={style.startDateCell}></div>
					<div className={style.endDateCell}></div>
					<div className={style.actionsCell}>
						{isDeleted ? (
							<button
								className={style.undoButton}
								onClick={() => {
									return undoDeleteCampaign(item.id);
								}}
								title="Undo delete"
							>
								<CustomIcon icon="undo" />
							</button>
						) : (
							<button
								className={style.deleteButton}
								onClick={() => {
									return deleteCampaign(item.id);
								}}
								title="Delete campaign"
							>
								<CustomIcon icon="trash" />
							</button>
						)}
					</div>
				</div>

				{isExpanded && item.children && item.children.length > 0 && item.children.map((child) => {
					const isBackfillRow = child.isBackfillIndicator;
					const isFolder = !isBackfillRow && child.itemType === 'folder';

					if (isFolder) {
						return (
							<FolderChildRow
								key={child.id}
								child={child}
								loopDate={loopDate}
								style={style}
								formatDate={formatDate}
							/>
						);
					}

					const rowClassName = isBackfillRow ?
						`${style.tableRow} ${style.childRow} ${style.backfillRow}` :
						`${style.tableRow} ${style.childRow}`;

					return (
						<div key={child.id} className={rowClassName}>
							<div className={style.nameCell}>
								<span className={isBackfillRow ? style.backfillName : style.childName} data-test-id={isBackfillRow ? 'loopDetailBackfillName' : 'loopDetailChildName'}>
									{child.name.startsWith('Folder:') ? (
										<>
											<CustomIcon icon={['far', 'folder']} />
											<span style={{ marginLeft: '0.75rem' }}>
												{child.name.substring('Folder:'.length).trim()}
											</span>
										</>
									) : (
										child.name
									)}
								</span>
							</div>
							<div className={style.durationCell}>
								{isBackfillRow ? '-' : child.duration}
							</div>
							<div className={style.slotsCell}>
								{child.slots}
							</div>
							<div className={style.orderCell}></div>
							<div className={style.startDateCell}>
								{isBackfillRow ? '' : (formatDate(child.startDate) || '')}
							</div>
							<div className={style.endDateCell}>
								{isBackfillRow ? '' : (formatDate(child.endDate) || '')}
							</div>
							<div className={style.actionsCell}>
								{/* Empty actions cell for child rows */}
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	// DraggableRow component for backfill content
	const DraggableBackfillRow = ({
		item,
		index,
		moveItemProp,
	}) => {
		const ref = useRef(null);

		const [
			{
				handlerId,
			},
			drop,
		] = useDrop({
			accept: ItemTypes.BACKFILL,
			collect(monitor) {
				return {
					handlerId: monitor.getHandlerId(),
				};
			},
			hover(dragItem, monitor) {
				if (!ref.current) {
					return;
				}
				const dragIndex = dragItem.index;
				const hoverIndex = index;

				if (dragIndex === hoverIndex) {
					return;
				}

				const hoverBoundingRect = ref.current?.getBoundingClientRect();
				const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
				const clientOffset = monitor.getClientOffset();
				const hoverClientY = clientOffset.y - hoverBoundingRect.top;

				if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
					return;
				}

				if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
					return;
				}

				moveItemProp(dragIndex, hoverIndex, ItemTypes.BACKFILL);
				dragItem.index = hoverIndex;
			},
		});

		const [
			{
				isDragging,
			},
			drag,
		] = useDrag({
			type: ItemTypes.BACKFILL,
			item: () => {
				return {
					id: item.id,
					index,
				};
			},
			collect: (monitor) => {
				return {
					isDragging: monitor.isDragging(),
				};
			},
		});

		const opacity = isDragging ? 0.5 : 1;
		drag(drop(ref));

		const isExpanded = item.expanded;
		const isDeleted = item.isDeleted;

		return (
			<div
				ref={ref}
				style={{
					opacity,
				}}
				data-handler-id={handlerId}
				className={`${style.draggableContainer} ${isDragging ? style.dragging : ''} ${isDeleted ? style.deletedItem : ''} ${(item.unavailable || item.suppressed) ? style.unavailableItem : ''}`}
			>
				<div className={style.backfillRow}>
					<div className={style.nameCell}>
						{item.children && item.children.length > 0 ? (
							<button
								className={style.expandButton}
								onClick={() => {
									return toggleBackfillExpanded(item.id);
								}}
								disabled={isDeleted}
							>
								<CustomIcon icon={isExpanded ? 'chevron-down' : 'chevron-right'} />
							</button>
						) : (
							<div className={style.expandButton} style={{
								visibility: 'hidden',
							}}>
								<CustomIcon icon="chevron-right" />
							</div>
						)}
						{!isDeleted && (
							<div className={style.dragHandle}>
								<CustomIcon icon="bars" />
							</div>
						)}
						<span
							className={`${style.parentName} ${isDeleted ? style.strikethrough : ''}`}
							title={item.unavailable ? 'This feed has been unpublished. It no longer plays and will be removed from this loop on the next save.' : (item.suppressed ? 'This feed is temporarily unavailable (publishing disabled or publisher access changed). It is skipped on players and will resume if the source is restored.' : undefined)}
							data-test-id={item.unavailable ? 'vclUnavailableProvider' : (item.suppressed ? 'vclSuppressedProvider' : 'loopDetailParentName')}
						>
							{item.name}
						</span>
					</div>
					<div className={style.durationCell}>
						{getProviderDuration(item)}
					</div>
					<div className={style.slotsCell}>
						<SlotsEditor
							value={item.slots}
							onUpdate={updateBackfillSlots}
							itemId={item.id}
							itemType="backfill"
						/>
					</div>
					<div className={style.orderCell}>
						{item.order}
					</div>
					<div className={style.startDateCell}></div>
					<div className={style.endDateCell}></div>
					<div className={style.actionsCell}>
						{isDeleted ? (
							<button
								className={style.undoButton}
								onClick={() => {
									return undoDeleteBackfillContent(item.id);
								}}
								title="Undo delete"
							>
								<CustomIcon icon="undo" />
							</button>
						) : (
							<button
								className={style.deleteButton}
								onClick={() => {
									return deleteBackfillContent(item.id);
								}}
								title="Delete backfill content"
							>
								<CustomIcon icon="trash" />
							</button>
						)}
					</div>
				</div>

				{isExpanded && item.children && item.children.length > 0 && item.children.map((child) => {
					const isBackfillIndicatorRow = child.isBackfillIndicator;

					return (
						<div key={child.id} className={`${style.backfillRow} ${style.childRow}`}>
							<div className={style.nameCell}>
								<span className={isBackfillIndicatorRow ? style.backfillName : style.childName} data-test-id={isBackfillIndicatorRow ? 'loopDetailBackfillName' : 'loopDetailChildName'}>
									{child.name.startsWith('Folder:') ? (
										<>
											<CustomIcon icon={['far', 'folder']} />
											<span style={{ marginLeft: '0.75rem' }}>
												{child.name.substring('Folder:'.length).trim()}
											</span>
										</>
									) : (
										child.name
									)}
								</span>
							</div>
							<div className={style.durationCell}>
								{isBackfillIndicatorRow ? '-' : child.duration}
							</div>
							<div className={style.slotsCell}>
								{child.slots}
							</div>
							<div className={style.orderCell}>
								{/* Orders are only shown for providers, not individual slides */}
							</div>
							<div className={style.startDateCell}>
								{isBackfillIndicatorRow ? '' : (formatDate(child.startDate) || '')}
							</div>
							<div className={style.endDateCell}>
								{isBackfillIndicatorRow ? '' : (formatDate(child.endDate) || '')}
							</div>
							<div className={style.actionsCell}>
								{/* Empty actions cell for child rows */}
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	if (!props.visualContentLoop) {
		return null;
	}

	return (
		<DndProvider backend={HTML5Backend}>
			<div className={style.container}>
				{/* Main Content */}
				<div className={style.mainContent}>
					<div className={style.header}>
						<div className={style.titleSection}>
							<h1 className={style.pageTitle}>{props.visualContentLoop.name}</h1>
							<div className={style.loopInfo}>
								<button
									type="button"
									className={style.resolutionButton}
									title={`Loop resolution: ${resolution} — click to toggle`}
									aria-label={`Loop resolution: ${resolution} — click to toggle`}
									onClick={handleToggleResolution}
									disabled={isSaving || isRefreshing}
									data-test-id="vcl-resolution-toggle"
								>
									<ResolutionToggleIcon resolution={resolution} size="2.25em" />
								</button>
								<label>
									<span className={style.loopDate}>Loop Date:</span>
									<input
										type="date"
										value={loopDate}
										// Greys out past dates in the calendar UI. It does not stop a typed or
										// pasted value — setLoopDate clamps for that.
										min={getCurrentDateString()}
										onChange={(e) => {
											return setLoopDate(e.target.value);
										}}
										className={style.dateInput}
										disabled={isSaving || isRefreshing}
										aria-label="Loop Date"
										data-test-id="vcl-loop-date"
									/>
								</label>
								<button
									className={style.refreshButton}
									title="Refresh"
									onClick={handleRefresh}
									disabled={isSaving || isRefreshing}
								>
									<span className={(isSaving || isRefreshing) ? style.spinning : ''}>
										<CustomIcon icon="sync-alt" color="white" />
									</span>
								</button>
							</div>
						</div>
					</div>

					{/* Content Table */}
					<div className={style.contentTable}>
						<div className={style.tableHeader}>
							<div className={style.nameHeader}>SLIDE DECKS / VISUAL CONTENT</div>
							<div className={style.durationHeader}>DURATION (PER SLOT)</div>
							<div className={style.slotsHeader}># SLOTS</div>
							<div className={style.orderHeader}>ORDER</div>
							<div className={style.startDateHeader}>STARTING</div>
							<div className={style.endDateHeader}>ENDING</div>
							<div className={style.actionsHeader}>DELETE</div>
						</div>

						<div className={style.tableBody}>
							{campaigns.length === 0 && (
								<div style={{
									padding:   '20px',
									textAlign: 'center',
									color:     '#666',
								}}>
									No providers found. Campaigns array length: {campaigns.length}
								</div>
							)}
							{campaigns
								.map((campaign, index) => {
									return (
										<DraggableCampaignRow
											key={campaign.id}
											item={campaign}
											index={index}
											moveItemProp={moveItem}
										/>
									);
								})}
						</div>

						{/* Add Content Button */}
						<div className={style.addContentRow}>
							<button
								className={style.addContentButton}
								data-test-id="addContentButton"
								onClick={() => {
									return openAddContentModal('content');
								}}
							>
								<CustomIcon icon="plus" />
								Add Content
							</button>
						</div>

						{/* Summary */}
						<div className={style.summaryRow}>
							<span className={style.summaryLabel}>
							Total Duration{(() => {
								// Check if any campaigns have backfill slots needed
									const hasBackfillNeeded = campaigns.some((campaign) => {
										return !campaign.isDeleted && campaign.children?.some((child) => {
											return child.isBackfillIndicator;
										});
									});

									// Check if there are any backfill providers with slots
									const hasBackfillProviders = backfillContent.some((b) => {
										return !b.isDeleted && b.slots > 0;
									});

									return hasBackfillNeeded && hasBackfillProviders ? ' (with Backfill)' : '';
								})()}: {totalDuration}
							</span>
							<span className={style.summaryLabel}>Number of Slots: {totalSlots}</span>
						</div>						{/* Backfill Content */}
						<div className={style.backfillSection}>
							<div className={style.backfillHeader}>
								<span className={style.backfillTitle}>Backfill Content</span>
								<button
									className={style.backfillDropdown}
									onClick={toggleBackfillCollapsed}
								>
									<CustomIcon icon={isBackfillCollapsed ? 'chevron-right' : 'chevron-down'} />
								</button>
							</div>

							{!isBackfillCollapsed && (
								<>
									<div className={style.backfillTable}>
										{backfillContent
											.map((backfillItem, index) => {
												return (
													<DraggableBackfillRow
														key={`${backfillItem.id}-${index}`}
														item={backfillItem}
														index={index}
														moveItemProp={moveItem}
													/>
												);
											})}
									</div>

									{/* Add Backfill Content Button */}
									<div className={style.addBackfillRow}>
										<button
											className={style.addBackfillButton}
											onClick={() => {
												return openAddContentModal('backfill');
											}}
										>
											<CustomIcon icon="plus" />
											Add Backfill Content
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>

				<div className={style.pageFooter}>
					<SaveButton location="footer" />
				</div>
			</div>

			{/* Add Content Modal */}
			<AddContentModal
				isOpen={showAddContentModal}
				onClose={closeAddContentModal}
				addContentType={addContentType}
				isLoadingSlideDecks={isLoadingSlideDecks}
				availableSlideDecks={availableSlideDecks}
				availablePublishedFeeds={availablePublishedFeeds}
				onAddProvider={handleAddProvider}
				account={props.visualContentLoop?.account}
			/>
		</DndProvider>
	);
};

export default VisualContentLoop;
